import SpinningBorder from './SpinningBorder';
import React, { useMemo } from 'react';
import { StyleSheet, Pressable, View, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import Icon from '@/components/Icon';

const createStyles = (colors, isDisabled) => StyleSheet.create({
  actionButton: {
    width: 34,
    height: 34,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    ...(Platform.OS === 'web' && { cursor: 'pointer' }),
    opacity: 1,
    backgroundColor: isDisabled ? 'transparent' : colors.solid_color_toast,
    ...(Platform.OS === 'web' && { transition: 'background-color 0.2s ease, opacity 0.2s ease' }),
  },
  actionButtonHovered: {
    backgroundColor: isDisabled ? colors.solid_color_tertiary : colors.solid_color_toast,
  },
  actionButtonLoadingWrapper: {
    width: 34,
    height: 34,
    justifyContent: 'center',
    alignItems: 'center',
    position: 'absolute',
  }
});



function ChatInputSendButton({
  onPress,
  isDisabled = false,
  isLoading = false,
  isInferencing = false,
  isCancelable = false,
}) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createStyles(colors, isDisabled), [colors, isDisabled]);
  const isInferencingAndNotCancelable = isInferencing && !isCancelable;

  // On iOS/Android, use onPressIn to fire immediately before keyboard
  // dismissal can interfere. Desktop targets should use the normal click path.
  const pressHandler = Platform.OS === 'ios' || Platform.OS === 'android'
    ? { onPressIn: onPress }
    : { onPress };

  return (
    <Pressable
      hitSlop={{ top: 5, bottom: 5, left: 5, right: 5 }}
      style={({ hovered }) => [
        styles.actionButton,
        hovered && styles.actionButtonHovered,
      ]}
      {...pressHandler}
      disabled={isDisabled}
      accessibilityRole="button"
      accessibilityLabel={isInferencing
        ? (t('chatInput.stopGenerating') || 'Stop generating')
        : (t('chatInput.sendMessage') || 'Send message')}
      accessibilityState={{ disabled: isDisabled, busy: isLoading }}
    >
      {isLoading && (
        <View style={styles.actionButtonLoadingWrapper}>
          <SpinningBorder />
        </View>
      )}
      {
        isInferencingAndNotCancelable ? null : (
          <Icon 
            name={isInferencing ? 'square' : 'arrow-up'}
            solid={isInferencing}
            size={isInferencing ? 14 : 16}
            color={isDisabled ? colors.text_color_tertiary : colors.text_color_alt}
          />
        )
      }
    </Pressable>
  );
}

export default React.memo(ChatInputSendButton);
